import { CircularProgress, Grid, Stack, Typography } from '@mui/material';
import React from 'react';

/**
 * Loading stages should be:
 * - Specific enough for developers to pick the right stage consistently
 * - Safe to show to external users (no identifiers or internal system details)
 *
 * Prefer naming based on the *user goal* or *page-level intent* (e.g. “switching study”,
 * “loading case list”) rather than low-level implementation details.
 */
export enum LoadingStage {
  SigningIn = 'Signing you in…',
  SigningOut = 'Signing you out…',
  CheckingAccess = 'Preparing your workspace…',
  /** Applying safe query-param reconciliation (filters/modes) after data is stable. */
  UpdatingQueryFilters = 'Updating filters…',
  SwitchingLab = 'Switching labs…',
  SwitchingStudy = 'Switching to the relevant study…',
  SwitchingCase = 'Opening the right case…',
  CheckingStudySlides = 'Checking study slides…',
  CheckingPendingSlides = 'Checking lost and found slides…',
  /** Ensuring the route/query is set to the slides view (e.g. enabling slidesMode). */
  SwitchingToSlidesView = 'Opening slides view…',
  UpdatingSlideFilters = 'Updating slide filters…',
  LoadingDashboard = 'Loading dashboard…',
  LoadingStudies = 'Loading studies…',
  LoadingCaseList = 'Loading cases…',
  LoadingSlidesList = 'Loading slides…',
  LoadingAccessions = 'Loading accessions…',
  LoadingUploads = 'Loading uploads…',
  LoadingUserCredentials = 'Loading user credentials…',
  LoadingCohortsAndQueries = 'Loading cohorts and saved queries…',
  LoadingTaskData = 'Loading task details…',
  LoadingResults = 'Loading results…',
  LoadingFeatures = 'Loading features…',
  LoadingConversation = 'Loading conversation…',
  LoadingReport = 'Loading report…',
  LoadingReportData = 'Loading report data…',
  LoadingOperationsSettings = 'Loading settings…',
  LoadingGallery = 'Loading gallery…',
  LoadingCase = 'Loading case…',
  LoadingSlide = 'Loading slide…',
  LoadingStudy = 'Loading study…',
  LoadingStudySettings = 'Loading study settings…',
  LoadingImages = 'Loading slide images…',
  PreparingViewerForRegistration = 'Preparing viewer…',
}

interface LoaderProps {
  size?: 'big' | 'small';
  stage?: LoadingStage;
  /** Renders a one-line spinner and text instead of filling the parent; use inside dialogs, forms, and panels. */
  inline?: boolean;
  /** Descriptive text for inline loaders when no stage fits, such as a live count. Takes precedence over `stage`. */
  message?: string;
  /** Percentage from 0 to 100 for known progress. Omit when the remaining work is unknown. */
  value?: number;
  ariaLabel?: string;
  testId?: string;
}

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

const Loader: React.FunctionComponent<React.PropsWithChildren<LoaderProps>> = ({
  size = 'big',
  stage,
  inline = false,
  message,
  value,
  ariaLabel,
  testId,
}) => {
  const label = message ?? stage;
  const progressProps = {
    'aria-label': ariaLabel ?? label ?? undefined,
    variant: value === undefined ? ('indeterminate' as const) : ('determinate' as const),
    value: value === undefined ? undefined : clampPercentage(value),
  };

  if (inline) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" role="status" aria-live="polite" data-testid={testId}>
        <CircularProgress size={20} {...progressProps} />
        {Boolean(label) && (
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Grid
      container
      direction="column"
      sx={{ width: '100%', height: '100%' }}
      alignItems="center"
      justifyContent="center"
      data-testid={testId}
    >
      {Boolean(label) && (
        <Grid item>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </Typography>
        </Grid>
      )}

      <Grid item>
        <CircularProgress size={size === 'big' ? 60 : 16} {...progressProps} />
      </Grid>
    </Grid>
  );
};

export default Loader;
